/**
 * Картинки достижений, уровней и тем (PLAN §3.2): F1 кладёт их в
 * public/achievements/<img_ver>/{96,256,480}/<id>.webp; nginx отдаёт папку
 * версии как immutable, поэтому любая замена картинки — новая img_ver.
 *
 * Анимированные (владелец 02.10: «не нужно видео — нужно гифы»): движение внутри
 * самого рисунка — вода течёт, свет мерцает, аура дышит — короткой бесшовной
 * петлёй, animated WebP тех же трёх размеров в <img_ver>/anim/{96,256,480}/<id>.webp.
 * Какие картинки движутся, говорит маленький anim/index.json (lib/anim): id → метка
 * содержимого. Метка — в адресе (?v=…): папка отдаётся immutable, а новый дубль
 * той же картинки должен прийти к тем, кто видел старый.
 */
export type AchImgSize = 96 | 256 | 480;

export const ACH_IMG_SIZES: readonly AchImgSize[] = [96, 256, 480];

export const achImageUrl = (id: string, size: AchImgSize, imgVer: string): string =>
    `/achievements/${encodeURIComponent(imgVer)}/${size}/${encodeURIComponent(id)}.webp`;

/** Список анимированных картинок папки img_ver (lib/anim грузит его один раз). */
export const achAnimIndexUrl = (imgVer: string): string => `/achievements/${encodeURIComponent(imgVer)}/anim/index.json`;

/** Анимированная картинка (animated WebP) того же размера; tag — метка содержимого из anim/index.json. */
export const achAnimUrl = (id: string, size: AchImgSize, imgVer: string, tag: string): string =>
    `/achievements/${encodeURIComponent(imgVer)}/anim/${size}/${encodeURIComponent(id)}.webp?v=${encodeURIComponent(tag)}`;

/** Анимированные картинки папки: id → метка содержимого. Нет в списке — картинка неподвижна. */
export type AchAnimIndex = ReadonlyMap<string, string>;

/**
 * Адрес картинки для <img>: есть в списке анимированных (и человек не просил
 * убрать движение — anim тогда не передают) — живая петля, иначе — обычный файл.
 * Список ещё не пришёл (undefined) — обычный файл: картинка не ждёт списка.
 */
export const achPictureSrc = (id: string, size: AchImgSize, imgVer: string, anim?: AchAnimIndex): string => {
    const tag = anim?.get(id);
    return tag ? achAnimUrl(id, size, imgVer, tag) : achImageUrl(id, size, imgVer);
};

/**
 * Файл под размер на экране: самый маленький, что не меньше px × 2 (экраны 2×).
 * 40 → 96 (значки), 110 → 256 (сетки), 210 → 480 (карточка).
 */
export const achFileSize = (cssPx: number): AchImgSize => ACH_IMG_SIZES.find((s) => s >= cssPx * 2) || 480;
